/**
 * The pre-registration draft: a wizard selection that outlives the page (KAN-1474).
 *
 * A visitor walks the first three wizard steps on the platform landing before
 * there is any shop. What they picked is kept in the BROWSER, in the shape of
 * ``POST /admin/onboarding/start`` minus everything own (operations, machines)
 * — so the day it is handed to a real tenant it is already the body the seeder
 * takes, and nothing has to be translated.
 *
 * Everything read back is treated as foreign input: storage may be blocked,
 * empty, written by an older build or edited by hand. ``parseWizardDraft``
 * keeps only what has the right shape and returns ``null`` for the rest; the
 * page then simply starts empty.
 */

import type {
  OnboardingDraft,
  OnboardingDraftMachine,
  OnboardingDraftOperation,
} from "@/api/types";

/** One shape on both sides of the wire — what is stored here is what is sent. */
export type WizardDraft = OnboardingDraft;
type WizardDraftMachine = OnboardingDraftMachine;
type WizardDraftOperation = OnboardingDraftOperation;

const STORAGE_KEY = "forgmatic.tryout.draft";
const VERSION = 1;
/** A draft older than this is a different visit; the catalog has moved on. */
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

const strings = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((v): v is string => typeof v === "string" && v !== "") : [];

export function parseWizardDraft(raw: unknown): WizardDraft | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const body = raw as Record<string, unknown>;
  const buyers =
    body.buyers === "b2b" || body.buyers === "b2c" || body.buyers === "both" ? body.buyers : null;
  const operations: WizardDraftOperation[] = [];
  for (const entry of Array.isArray(body.operations) ? body.operations : []) {
    if (!entry || typeof entry !== "object") continue;
    const op = entry as Record<string, unknown>;
    if (typeof op.slug !== "string" || op.slug === "") continue;
    const machines: WizardDraftMachine[] = [];
    for (const rawMachine of Array.isArray(op.machines) ? op.machines : []) {
      if (!rawMachine || typeof rawMachine !== "object") continue;
      const machine = rawMachine as Record<string, unknown>;
      // An own machine has no ``catalog_key``; the draft carries none.
      if (typeof machine.catalog_key !== "string" || machine.catalog_key === "") continue;
      const quantity = typeof machine.quantity === "number" ? Math.trunc(machine.quantity) : 1;
      machines.push({
        catalog_key: machine.catalog_key,
        addon_keys: strings(machine.addon_keys),
        quantity: Math.max(1, Math.min(99, quantity || 1)),
      });
    }
    operations.push({ slug: op.slug, machines, material_codes: strings(op.material_codes) });
  }
  return {
    buyers,
    country_codes: strings(body.country_codes),
    carrier_codes: strings(body.carrier_codes),
    operations,
  };
}

/** ``true`` when the draft names nothing at all — not worth keeping or offering. */
export const isEmptyDraft = (draft: WizardDraft): boolean =>
  draft.operations.length === 0 &&
  draft.country_codes.length === 0 &&
  draft.carrier_codes.length === 0 &&
  draft.buyers === null;

export function readStoredDraft(): WizardDraft | null {
  try {
    const text = window.localStorage.getItem(STORAGE_KEY);
    if (!text) return null;
    const stored = JSON.parse(text) as { v?: unknown; saved_at?: unknown; draft?: unknown };
    if (stored.v !== VERSION || typeof stored.saved_at !== "number") return null;
    if (Date.now() - stored.saved_at > MAX_AGE_MS) return null;
    const draft = parseWizardDraft(stored.draft);
    return draft && !isEmptyDraft(draft) ? draft : null;
  } catch {
    return null;
  }
}

export function writeStoredDraft(draft: WizardDraft): void {
  try {
    if (isEmptyDraft(draft)) {
      window.localStorage.removeItem(STORAGE_KEY);
      return;
    }
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ v: VERSION, saved_at: Date.now(), draft }),
    );
  } catch {
    // Private window, blocked site data, full quota — the page works without it.
  }
}

/** The draft has been handed to the server with a signup; it lives there now. */
export function clearStoredDraft(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to forget where nothing could be kept.
  }
}
